import { Button, Group, Stack, Text } from "@mantine/core";
import { useCallback, useEffect, useImperativeHandle, useRef, useState, type RefObject } from "react";

/**
 * Touch/stylus signature pad on a canvas (REQ-CAP-1).
 *
 * Hand-rolled rather than a library, for three reasons that matter on a phone:
 * the canvas is sized to devicePixelRatio so strokes are not blurry on a
 * retina screen; Pointer Events cover finger, stylus and mouse with one code
 * path; and `touch-action: none` stops the browser treating a signing gesture
 * as a page scroll, which is the single most common way these pads feel
 * broken.
 */

export interface SignaturePadHandle {
  clear(): void;
  toDataUrl(): string | undefined;
  isEmpty(): boolean;
}

interface Props {
  ref?: RefObject<SignaturePadHandle | null>;
  onChange?: (hasSignature: boolean) => void;
  ariaLabel?: string;
}

export function SignaturePad({ ref, onChange, ariaLabel = "Signature" }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const dirtyRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);

  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;

    // Preserve whatever is already drawn across a resize (orientation change).
    const previous = dirtyRef.current ? canvas.toDataURL() : undefined;

    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#111827";

    if (previous) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, rect.width, rect.height);
      img.src = previous;
    }
  }, []);

  useEffect(() => {
    setupCanvas();
    const observer = new ResizeObserver(setupCanvas);
    if (canvasRef.current) observer.observe(canvasRef.current);
    return () => observer.disconnect();
  }, [setupCanvas]);

  const positionOf = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLCanvasElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = positionOf(event);
    drawingRef.current = true;
    ctx.beginPath();
    ctx.moveTo(x, y);
    // A single tap is a legitimate mark (a dot), so record it immediately.
    ctx.lineTo(x + 0.1, y + 0.1);
    ctx.stroke();
    if (!dirtyRef.current) {
      dirtyRef.current = true;
      setHasSignature(true);
      onChange?.(true);
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const { x, y } = positionOf(event);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endStroke = () => {
    drawingRef.current = false;
  };

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const ratio = window.devicePixelRatio || 1;
    ctx.clearRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    dirtyRef.current = false;
    setHasSignature(false);
    onChange?.(false);
  }, [onChange]);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      isEmpty: () => !dirtyRef.current,
      toDataUrl: () =>
        dirtyRef.current ? canvasRef.current?.toDataURL("image/png") : undefined,
    }),
    [clear],
  );

  return (
    <Stack gap="xs">
      <canvas
        ref={canvasRef}
        className="sigpad__canvas"
        role="img"
        aria-label={ariaLabel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
        onPointerLeave={endStroke}
      />
      <Group justify="space-between" align="center" wrap="nowrap">
        <Text fz="sm" c={hasSignature ? "teal.8" : "gray.6"}>
          {hasSignature ? "Signature captured" : "Sign in the box above"}
        </Text>
        <Button variant="default" size="sm" onClick={clear} disabled={!hasSignature}>
          Clear
        </Button>
      </Group>
    </Stack>
  );
}
